import {inject, injectable} from "inversify";
import {EffectType} from "@/modules/club-grounds/types";
import {GroundSlot} from "@/modules/club-grounds/entity";
import GroundSlotConfigProvider from "@/modules/club-grounds/services/GroundSlotConfigProvider";
import {Collection} from "collect.js";
import {EffectTypeEnum} from "@/modules/club-grounds/enums";

@injectable()
export default class AggregatedEffectProvider {
  constructor(
    @inject(GroundSlotConfigProvider) private groundSlotConfigProvider: GroundSlotConfigProvider,
  ) {
  }

  public getSingleValue(effectType: EffectTypeEnum, slot: GroundSlot) {
    const effects = this.get([slot]);
    const effect = effects.find(e => e.type === effectType);

    return effect ? effect.value : null;
  }

  public async getValue(effectType: EffectTypeEnum) {
    const slots = await GroundSlot
      .createQueryBuilder('slot')
      .where('slot.type IS NOT NULL AND slot.developedAt IS NULL')
      .getMany();

    const effects = this.get(slots);
    const effect = effects.find(e => e.type === effectType);

    return effect ? effect.value : null;
  }

  public get(groundSlots: GroundSlot[]): EffectType[] {
    return collect(groundSlots)
      .map(slot => {
        const configs = this.groundSlotConfigProvider.get(slot);
        const config = configs.find(c => c.id === slot.type);

        if (config) {
          return config.effects;
        }

        return [];
      })
      .flatten(1)
      .groupBy('type')
      .map((item: unknown) => {
        return (item as Collection<EffectType>)
          .reduce((memo: EffectType | null, item) => {
            if (!memo) {
              return item;
            }

            return Object.assign({}, item, {value: memo.value + item.value});
          });
      })
      .toArray();
  }
}
